import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Bell, 
  Clock, 
  Settings, 
  User, 
  ShoppingCart, 
  AlertTriangle, 
  CheckCircle,
  Smartphone,
  Monitor,
  Tablet,
  DollarSign
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

type NotificationRow = Database['public']['Tables']['notifications']['Row'];
type ActivityLogRow = Database['public']['Tables']['activity_log']['Row'];
type UserProfileRow = Database['public']['Tables']['user_profiles']['Row'];

interface AppNotification {
  id: string;
  type: 'customer_visit' | 'new_sale' | 'payment_reminder' | 'low_stock' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'high' | 'medium' | 'low';
}

interface CompanyDevice {
  id: string;
  name: string;
  type: 'mobile' | 'desktop' | 'tablet';
  owner: string;
  location: string;
  online: boolean;
}

interface DeviceSettings {
  customerNotifications: {
    enabled: boolean;
    selectedDevices: string[];
  };
}

const companyDevices: CompanyDevice[] = [
  {
    id: '1',
    name: 'Satış Kasası - Ana',
    type: 'desktop',
    owner: 'Mehmet Öz',
    location: 'Kasa 1',
    online: true
  },
  {
    id: '2',
    name: 'Satış Kasası - Yedek',
    type: 'desktop',
    owner: 'Elif Demir',
    location: 'Kasa 2',
    online: true
  },
  {
    id: '3',
    name: 'Müdür Telefonu',
    type: 'mobile',
    owner: 'Mehmet Öz',
    location: 'Ofis',
    online: true
  },
  {
    id: '4',
    name: 'Satış Telefonu 1',
    type: 'mobile',
    owner: 'Elif Demir',
    location: 'Satış Alanı',
    online: false
  },
  {
    id: '5',
    name: 'Satış Telefonu 2',
    type: 'mobile',
    owner: 'Can Aydın',
    location: 'Satış Alanı',
    online: true
  },
  {
    id: '6',
    name: 'Depo Tableti',
    type: 'tablet',
    owner: 'Ayşe Kaya',
    location: 'Depo',
    online: true
  },
  {
    id: '7',
    name: 'Vitrin Tableti',
    type: 'tablet',
    owner: 'Genel Kullanım',
    location: 'Vitrin',
    online: false
  }
];


const notificationConfig = {
  customer_visit: { 
    icon: User, 
    color: 'bg-blue-500/10 text-blue-600 border-blue-200',
    label: 'Müşteri Ziyareti'
  },
  new_sale: { 
    icon: ShoppingCart, 
    color: 'bg-green-500/10 text-green-600 border-green-200',
    label: 'Yeni Satış'
  },
  payment_reminder: { 
    icon: DollarSign, 
    color: 'bg-orange-500/10 text-orange-600 border-orange-200',
    label: 'Ödeme Hatırlatması'
  },
  low_stock: { 
    icon: AlertTriangle, 
    color: 'bg-yellow-500/10 text-yellow-600 border-yellow-200',
    label: 'Stok Uyarısı'
  },
  system: { 
    icon: Settings, 
    color: 'bg-purple-500/10 text-purple-600 border-purple-200',
    label: 'Sistem'
  }
};

const priorityConfig = {
  high: { color: 'bg-red-500/10 text-red-600', label: 'Yüksek' },
  medium: { color: 'bg-yellow-500/10 text-yellow-600', label: 'Orta' },
  low: { color: 'bg-gray-500/10 text-gray-600', label: 'Düşük' }
};

export default function Notifications() {
  const { toast } = useToast();
  const { companyId, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceSettings, setDeviceSettings] = useState<DeviceSettings>({
    customerNotifications: {
      enabled: true,
      selectedDevices: ['1', '3'] // Default to main register and manager phone
    }
  });
  const [realDevices, setRealDevices] = useState<CompanyDevice[]>([]);

  // Load device settings from localStorage
  const loadDeviceSettings = () => {
    try {
      const saved = localStorage.getItem(`notification-settings-${companyId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        setDeviceSettings(parsed);
      }
    } catch (error) {
      console.error('Error loading device settings:', error);
    }
  };

  // Save device settings to localStorage
  const saveDeviceSettings = (settings: DeviceSettings) => {
    try {
      localStorage.setItem(`notification-settings-${companyId}`, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving device settings:', error);
    }
  };

  // Fetch real devices from user profiles and create device list
  const fetchDevices = async () => {
    if (!companyId) return;

    try {
      // Fetch users to create device list based on real company users
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, full_name, role, email')
        .eq('company_id', companyId);

      if (usersError) throw usersError;

      // Create device list based on users and mock devices
      const devices: CompanyDevice[] = [
        // System devices
        ...companyDevices.filter(device => device.type === 'desktop'),
        // User devices based on real users
        ...(users || []).map((user, index) => ({
          id: `user-${user.id}`,
          name: `${user.full_name} - ${user.role === 'admin' ? 'Yönetici' : 'Satış'} Telefonu`,
          type: 'mobile' as const,
          owner: user.full_name || 'Bilinmeyen',
          location: user.role === 'admin' ? 'Ofis' : 'Satış Alanı',
          online: Math.random() > 0.3 // Random online status for demo
        }))
      ];

      setRealDevices(devices);
    } catch (error) {
      console.error('Error fetching devices:', error);
      // Fallback to mock devices
      setRealDevices(companyDevices);
    }
  };

  // Fetch notifications from database
  const fetchNotifications = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch real notifications from notifications table
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select(`
          *,
          user_profiles!notifications_recipient_user_id_fkey(full_name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (notificationsError) throw notificationsError;

      // Fetch recent activity log entries to create system notifications
      const { data: activityData, error: activityError } = await supabase
        .from('activity_log')
        .select(`
          *,
          user_profiles!activity_log_actor_user_id_fkey(full_name)
        `)
        .eq('company_id', companyId)
        .order('ts', { ascending: false })
        .limit(20);

      if (activityError) throw activityError;

      // Transform database notifications
      const dbNotifications: AppNotification[] = (notificationsData || []).map(notif => ({
        id: notif.id,
        type: mapNotificationTypeFromDatabase(notif.title, notif.body),
        title: notif.title,
        message: notif.body,
        timestamp: new Date(notif.created_at),
        read: !!notif.read_at,
        priority: mapPriorityFromDatabase(notif.priority || 'medium')
      }));

      // Transform activity log entries into notifications
      const activityNotifications: AppNotification[] = (activityData || [])
        .filter(activity => shouldCreateNotificationFromActivity(activity))
        .map(activity => ({
          id: `activity-${activity.id}`,
          type: mapActivityToNotificationType(activity.action, activity.entity),
          title: generateNotificationTitle(activity),
          message: generateNotificationMessage(activity),
          timestamp: new Date(activity.ts),
          read: true, // Activity notifications are considered read by default
          priority: 'low' as const
        }));

      // Combine and sort all notifications
      const allNotifications = [...dbNotifications, ...activityNotifications]
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 50); // Limit to 50 most recent

      setNotifications(allNotifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Bildirimler yüklenirken bir hata oluştu');
      // Keep existing notifications on error, don't fallback to mock data
      if (notifications.length === 0) {
        setNotifications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const mapNotificationTypeFromDatabase = (title: string, body: string): AppNotification['type'] => {
    const lowerTitle = title.toLowerCase();
    const lowerBody = body.toLowerCase();

    if (lowerTitle.includes('müşteri') && (lowerTitle.includes('giriş') || lowerTitle.includes('ziyaret'))) {
      return 'customer_visit';
    }
    if (lowerTitle.includes('satış') || lowerBody.includes('satış')) {
      return 'new_sale';
    }
    if (lowerTitle.includes('ödeme') || lowerBody.includes('ödeme')) {
      return 'payment_reminder';
    }
    if (lowerTitle.includes('stok') || lowerBody.includes('stok')) {
      return 'low_stock';
    }
    return 'system';
  };

  const mapPriorityFromDatabase = (priority: string): AppNotification['priority'] => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'high';
      case 'medium':
      case 'normal':
        return 'medium';
      case 'low':
      default:
        return 'low';
    }
  };

  const shouldCreateNotificationFromActivity = (activity: ActivityLogRow): boolean => {
    // Only create notifications for specific activities
    const notifiableActions = [
      'customer_added',
      'sale_completed',
      'product_low_stock',
      'customer_visit_started',
      'package_created',
      'payment_received'
    ];
    return notifiableActions.includes(activity.action);
  };

  const mapActivityToNotificationType = (action: string, entity: string): AppNotification['type'] => {
    if (action.includes('customer') || entity === 'customers') {
      return 'customer_visit';
    }
    if (action.includes('sale') || entity === 'sales') {
      return 'new_sale';
    }
    if (action.includes('payment') || entity === 'sale_payments') {
      return 'payment_reminder';
    }
    if (action.includes('stock') || action.includes('inventory')) {
      return 'low_stock';
    }
    return 'system';
  };

  const generateNotificationTitle = (activity: ActivityLogRow): string => {
    switch (activity.action) {
      case 'customer_added':
        return 'Yeni Müşteri Eklendi';
      case 'sale_completed':
        return 'Satış Tamamlandı';
      case 'product_low_stock':
        return 'Düşük Stok Uyarısı';
      case 'customer_visit_started':
        return 'Müşteri Ziyareti';
      case 'package_created':
        return 'Yeni Paket Oluşturuldu';
      case 'payment_received':
        return 'Ödeme Alındı';
      default:
        return 'Sistem Bildirimi';
    }
  };

  const generateNotificationMessage = (activity: ActivityLogRow): string => {
    const actorName = (activity as any).user_profiles?.full_name || 'Sistem';
    const detail = activity.detail as any;
    
    switch (activity.action) {
      case 'customer_added':
        return `${actorName} tarafından yeni müşteri eklendi`;
      case 'sale_completed':
        return `${actorName} tarafından ${detail?.total ? `₺${detail.total.toLocaleString('tr-TR')} tutarında ` : ''}satış tamamlandı`;
      case 'product_low_stock':
        return `${detail?.product_name || 'Bir ürün'} için stok seviyesi kritik`;
      case 'customer_visit_started':
        return `${detail?.customer_name || 'Bir müşteri'} mağazaya giriş yaptı`;
      case 'package_created':
        return `${actorName} tarafından yeni paket oluşturuldu`;
      case 'payment_received':
        return `${detail?.amount ? `₺${detail.amount.toLocaleString('tr-TR')} ` : ''}ödeme alındı`;
      default:
        return `${actorName} tarafından ${activity.entity} güncellemesi yapıldı`;
    }
  };

  useEffect(() => {
    if (!authLoading && companyId) {
      void loadDeviceSettings();
      void fetchDevices();
      void fetchNotifications();

      // Set up real-time subscription for notifications
      const notificationsSubscription = supabase
        .channel('notifications')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `company_id=eq.${companyId}`
          },
          (payload) => {
            console.log('Notification change:', payload);
            // Refresh notifications when there's a change
            void fetchNotifications();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'activity_log',
            filter: `company_id=eq.${companyId}`
          },
          (payload) => {
            console.log('Activity log change:', payload);
            // Refresh notifications when there's a new activity
            fetchNotifications();
          }
        )
        .subscribe();

      // Cleanup subscription on unmount
      return () => {
        supabase.removeChannel(notificationsSubscription);
      };
    }
  }, [companyId, authLoading]);

  const markAsRead = async (notificationId: string) => {
    // Update local state immediately for better UX
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );

    // Only update database for real notifications (not activity-generated ones)
    if (!notificationId.startsWith('activity-')) {
      try {
        const { error } = await supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('id', notificationId)
          .eq('company_id', companyId || '');

        if (error) {
          console.error('Error marking notification as read:', error);
          // Revert the change if database update fails
          setNotifications(prev => 
            prev.map(notif => 
              notif.id === notificationId ? { ...notif, read: false } : notif
            )
          );
        }
      } catch (err) {
        console.error('Error updating notification:', err);
      }
    }
  };

  const markAllAsRead = async () => {
    // Update local state immediately
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );

    // Update database for real notifications
    try {
      const realNotificationIds = notifications
        .filter(notif => !notif.id.startsWith('activity-') && !notif.read)
        .map(notif => notif.id);

      if (realNotificationIds.length > 0) {
        const { error } = await supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .in('id', realNotificationIds)
          .eq('company_id', companyId || '');

        if (error) {
          console.error('Error marking all notifications as read:', error);
        }
      }
    } catch (err) {
      console.error('Error updating notifications:', err);
    }

    toast({
      title: "Tüm bildirimler okundu olarak işaretlendi",
    });
  };

  const updateDeviceSettings = (setting: keyof DeviceSettings['customerNotifications'], value: any) => {
    const newSettings = {
      ...deviceSettings,
      customerNotifications: {
        ...deviceSettings.customerNotifications,
        [setting]: value
      }
    };
    
    setDeviceSettings(newSettings);
    saveDeviceSettings(newSettings);
    
    toast({
      title: "Ayarlar güncellendi",
      description: "Cihaz bildirimi ayarları kaydedildi",
    });
  };

  const toggleDevice = (deviceId: string) => {
    const currentDevices = deviceSettings.customerNotifications.selectedDevices;
    const newDevices = currentDevices.includes(deviceId)
      ? currentDevices.filter(id => id !== deviceId)
      : [...currentDevices, deviceId];
    
    const newSettings = {
      ...deviceSettings,
      customerNotifications: {
        ...deviceSettings.customerNotifications,
        selectedDevices: newDevices
      }
    };
    
    setDeviceSettings(newSettings);
    saveDeviceSettings(newSettings);
    
    toast({
      title: "Cihaz ayarları güncellendi",
      description: "Seçili cihazlar kaydedildi",
    });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getDeviceIcon = (type: CompanyDevice['type']) => {
    switch (type) {
      case 'mobile': return Smartphone;
      case 'desktop': return Monitor;
      case 'tablet': return Tablet;
    }
  };

  const selectedDevicesCount = deviceSettings.customerNotifications.selectedDevices.length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center relative">
              <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Bildirimler</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Uygulama bildirimleri ve cihaz ayarları
              </p>
            </div>
          </div>
          
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Tümünü Okundu İşaretle
            </Button>
          )}
        </div>

        {/* Device Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Müşteri Bildirimi Ayarları
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Müşteri Girişi Bildirimleri</Label>
                <p className="text-sm text-muted-foreground">
                  Müşteri mağazaya girdiğinde bildirim al
                </p>
              </div>
              <Switch
                checked={deviceSettings.customerNotifications.enabled}
                onCheckedChange={(checked) => updateDeviceSettings('enabled', checked)}
              />
            </div>
            
            {deviceSettings.customerNotifications.enabled && (
              <>
                <Separator />
                <div>
                  <Label className="text-base font-medium mb-3 block">
                    Bildirim Cihazları ({selectedDevicesCount} seçili)
                  </Label>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {realDevices.length === 0 ? (
                      <div className="text-center py-8">
                        <Smartphone className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                        <p className="text-sm text-muted-foreground">
                          Cihazlar yükleniyor...
                        </p>
                      </div>
                    ) : (
                      realDevices.map((device) => {
                      const DeviceIcon = getDeviceIcon(device.type);
                      const isSelected = deviceSettings.customerNotifications.selectedDevices.includes(device.id);
                      
                      return (
                        <div
                          key={device.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                            isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                          } ${!device.online ? 'opacity-60' : ''}`}
                          onClick={() => device.online && toggleDevice(device.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                device.online ? 'bg-success/10' : 'bg-muted'
                              }`}>
                                <DeviceIcon className={`w-4 h-4 ${
                                  device.online ? 'text-success' : 'text-muted-foreground'
                                }`} />
                              </div>
                              <div>
                                <h4 className="font-medium text-sm">{device.name}</h4>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{device.owner}</span>
                                  <span>•</span>
                                  <span>{device.location}</span>
                                  <span>•</span>
                                  <Badge 
                                    variant="outline" 
                                    className={`h-5 ${
                                      device.online 
                                        ? 'bg-success/10 text-success border-success/30' 
                                        : 'bg-muted text-muted-foreground'
                                    }`}
                                  >
                                    {device.online ? 'Çevrimiçi' : 'Çevrimdışı'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            
                            {device.online && (
                              <Switch
                                checked={isSelected}
                                onCheckedChange={() => toggleDevice(device.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                        </div>
                      );
                    }))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Müşteri girişi bildirimlerinin gönderileceği cihazları seçin. 
                    Sadece çevrimiçi cihazlar seçilebilir.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Notifications List */}
        <Card>
          <CardHeader>
            <CardTitle>Son Bildirimler ({loading ? '...' : notifications.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <h3 className="text-lg font-medium mb-2">Bildirimler yükleniyor...</h3>
                <p className="text-muted-foreground">
                  Lütfen bekleyiniz
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-red-500 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2 text-red-600">Hata oluştu</h3>
                <p className="text-muted-foreground mb-4">
                  {error}
                </p>
                <Button onClick={fetchNotifications} variant="outline">
                  Tekrar Dene
                </Button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Henüz bildirim yok</h3>
                <p className="text-muted-foreground">
                  Uygulama bildirimleri burada görünecek
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => {
                  const config = notificationConfig[notification.type];
                  const priorityStyle = priorityConfig[notification.priority];
                  const Icon = config.icon;
                  
                  return (
                    <div
                      key={notification.id}
                      className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:bg-accent/5 ${
                        !notification.read ? 'bg-primary/5 border-primary/20' : 'bg-card'
                      }`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className={`font-medium ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {notification.title}
                            </h4>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className={priorityStyle.color}>
                                {priorityStyle.label}
                              </Badge>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-primary rounded-full"></div>
                              )}
                            </div>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2">
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>
                                {format(notification.timestamp, 'PPp', { locale: tr })}
                              </span>
                            </div>
                            <Badge variant="outline" className={config.color}>
                              {config.label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}